import { Component, DoCheck, ElementRef, HostListener, Input, IterableDiffers, ViewChild } from '@angular/core';
import { buttonSize } from '@models/button-options.enum';
import { ElementAttribute, lineJoinStyleList, lineStyleList } from '@models/editor';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { mixedString } from 'icreate/common/models/mimic';

@Component({
    selector: 'app-stroke-mixed',
    templateUrl: './stroke-mixed.component.html',
    styleUrls: ['./stroke-mixed.component.css'],
    standalone: false,
})
export class StorkMixedComponent implements DoCheck {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly BUTTON_SMALL_SIZE = buttonSize.small;
    readonly LINE_STYLE_LIST = Object.keys(lineStyleList);
    readonly LINE_JOIN_STYLE_LIST = lineJoinStyleList.map(item => item.name);
    readonly STROKE_WIDTH_MAX: number = 100;
    readonly STROKE_WIDTH_MIN: number = 0;
    readonly MIXED_STRING = mixedString;

    @Input() selectedElements;
    @ViewChild('styleList') styleList: ElementRef;
    @ViewChild('input') strokeWidthInput: ElementRef;

    strokeWidth: number | string;

    strokeStyle: string;
    showingStyleList: boolean = false;

    joinStyle: string;

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.styleList?.nativeElement.contains(event.target) && this.showingStyleList) {
            this.showingStyleList = false;
        }
    }

    private iterableDiffer;

    constructor(
        private iterableDiffers: IterableDiffers,
        private keyFilterService: KeyFilterSerivce
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            if (this.selectedElements.length > 1) {
                this.strokeWidth = mixedString;
                return;
            } else {
                if (this.connectorType) {
                    const strokeElement = this.selectedElements[0].querySelector('[isstroke="true"]') as HTMLElement;
                    this.strokeStyle = 'line-solid';
                    this.joinStyle = this.LINE_JOIN_STYLE_LIST[0];
                    this.strokeWidth = strokeElement.getAttribute(ElementAttribute.strokeWidth) || 1;
                    return;
                }

                this.strokeWidth = this.selectedElements[0].getAttribute(ElementAttribute.strokeWidth) || 1;
                const strokeStyle = this.selectedElements[0].getAttribute(ElementAttribute.strokeDashArray);
                if (!strokeStyle) {
                    this.strokeStyle = 'line-solid';
                } else {
                    this.LINE_STYLE_LIST.forEach(type => {
                        if (lineStyleList[type] === strokeStyle) {
                            this.strokeStyle = type;
                        }
                    });
                }
                const lineJoin = this.selectedElements[0].getAttribute(ElementAttribute.lineJoinStyle);
                if (!lineJoin) {
                    this.joinStyle = this.LINE_JOIN_STYLE_LIST[0];
                } else {
                    const match = lineJoinStyleList.find(item => item.svgIcon === lineJoin);
                    if (match) {
                        this.joinStyle = match.name;
                    }
                }
            }
        }
    }

    openStyleList(event: MouseEvent) {
        event.stopImmediatePropagation();
        this.showingStyleList = !this.showingStyleList;
    }

    handleStrokeChange() {
        if (!this.strokeWidth) this.strokeWidth = String(this.STROKE_WIDTH_MIN);
        this.selectedElements.forEach(element => {
            const connectorType = element.getAttribute(ElementAttribute.connectorType);
            if (connectorType) {
                const stokeElements = element.querySelectorAll('[isstroke="true"]');
                stokeElements.forEach(element => {
                    if (!element.getAttribute('data-bbox-ignore')) {
                        element.setAttribute(ElementAttribute.strokeWidth, this.strokeWidth);
                    }
                });
            } else element.setAttribute(ElementAttribute.strokeWidth, this.strokeWidth);
        });
    }

    setStrokeWidthElem() {
        const createValue = Number(this.strokeWidth).toFixed(0);
        if (Number(createValue) < 0) {
            this.strokeWidthInput.nativeElement.value = String(this.STROKE_WIDTH_MIN);
            this.strokeWidth = String(this.STROKE_WIDTH_MIN);
        } else if (Number(createValue) > Number(this.STROKE_WIDTH_MAX)) {
            this.strokeWidthInput.nativeElement.value = String(this.STROKE_WIDTH_MAX);
            this.strokeWidth = String(this.STROKE_WIDTH_MAX);
        } else {
            this.strokeWidthInput.nativeElement.value = this.strokeWidth;
        }
        this.handleStrokeChange();
    }

    handleStyleChange(style: string) {
        this.LINE_STYLE_LIST.forEach(type => {
            if (style === type) {
                if (!lineStyleList[style]) {
                    this.selectedElements.forEach(element => {
                        element.removeAttribute(ElementAttribute.strokeDashArray);
                    });
                } else {
                    this.selectedElements.forEach(element => {
                        element.setAttribute(ElementAttribute.strokeDashArray, lineStyleList[style]);
                    });
                }
                this.strokeStyle = style;
            }
        });
    }

    handleJoinChange(style: string) {
        const matched = lineJoinStyleList.find(item => item.name === style);
        if (matched) {
            this.selectedElements.forEach(element => {
                element.setAttribute(ElementAttribute.lineJoinStyle, matched.svgIcon);
            });
        }
        this.joinStyle = style;
    }

    getTooltip(name: string): string {
        const match = lineJoinStyleList.find(item => item.name === name);
        return match?.tooltip || '';
    }

    validStrokeKeyDown(event: KeyboardEvent) {
        if (this.checkStrokeWidthStringLimit() && event.key !== 'Backspace') {
            event.preventDefault();
            return;
        }

        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, [...this.ALLOWED_KEYS, '.']);
        if (shouldBlockKey) event.preventDefault();
    }

    checkStrokeWidthStringLimit() {
        return String(this.strokeWidth).length >= 20;
    }

    get connectorType() {
        if (!this.selectedElements || this.selectedElements.length != 1) return false;
        const element = this.selectedElements[0];
        const connectorType = element.getAttribute(ElementAttribute.connectorType);
        return connectorType;
    }
}
